import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Dialog,
  TextField,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Input,
  IconButton,
  Typography,
  CircularProgress,
  Modal,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  fetchReceipt,
  uploadReceipt,
} from "../../../../contents/redux/actions/paymentRequestActions";
import { toast } from "react-toastify";

const VoucherModal = ({ open, onClose, pdfUrl, requestId, paymentCode }) => {
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = useState(null); // Changed to singular
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const requestDetails = useSelector(
    (state) => state.paymentRequestDetails?.requestDetails || null,
  );
  const receipt = useSelector((state) => state.paymentRequest?.receipt || null);
  const userId =
    useSelector((state) => state.auth?.userId) ||
    localStorage.getItem("userId");

  console.log("VoucherModal - UserId: ", userId);
  console.log("VoucherModal - receipt path: ", receipt);
  const [isLoadingReceipt, setIsLoadingReceipt] = useState(false);

  useEffect(() => {
    console.log("VoucherModal mounted:", {
      open,
      pdfUrl,
      requestId,
      paymentCode,
    });
    if (requestId) {
      setIsLoadingReceipt(true);
      dispatch(fetchReceipt(requestId)).finally(() =>
        setIsLoadingReceipt(false),
      );
    }
    console.log("VoucherModal - Redux state - requestDetails:", requestDetails);
    console.log("VoucherModal - Redux state - receipt:", receipt);
  }, [dispatch, requestId]);

  useEffect(() => {
    console.log("VoucherModal - Redux state - receipt updated:", receipt);
  }, [receipt]);

  const getFileIcon = (contentType) => {
    return contentType?.startsWith("image/") ? (
      <ImageIcon />
    ) : (
      <PictureAsPdfIcon />
    );
  };

  const handleOpenLightbox = (event) => {
    event.preventDefault();
    if (receipt?.contentType?.startsWith("image/")) {
      console.log("Opening lightbox for receipt:", receipt.url);
      setIsLightboxOpen(true);
    } else {
      toast.info("Lightbox is only available for image receipts.");
    }
  };

  const handleCloseLightbox = () => {
    console.log("Closing lightbox");
    setIsLightboxOpen(false);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0]; // Take only the first file
    setSelectedFile(file || null);
  };

  const handleUpload = async () => {
    if (!requestId) {
      toast.error("Request ID is missing.");
      return;
    }
    if (!userId) {
      toast.error("User ID is missing.");
      return;
    }
    if (!selectedFile) {
      toast.error("Please select a file to upload.");
      return;
    }

    setIsUploading(true);
    try {
      console.log(
        "VoucherModal - Uploading file:",
        selectedFile.name,
        selectedFile.type,
        selectedFile.size,
      );
      await dispatch(uploadReceipt(requestId, userId, [selectedFile]));
      setSelectedFile(null);
      await dispatch(fetchReceipt(requestId));
      toast.success("Receipt uploaded successfully.");
    } catch (error) {
      console.error("Upload error:", error);
      const errorMessage =
        error.response?.data ||
        error.message.includes("missing required fields")
          ? "Failed to upload receipt: Invalid server response."
          : error.message || "Failed to upload receipt.";
      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Voucher View
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {pdfUrl ? (
            <Box sx={{ height: "80vh" }}>
              <iframe
                src={pdfUrl}
                title="Payment Voucher"
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            </Box>
          ) : (
            <Typography>No voucher available.</Typography>
          )}
          {receipt?.url ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1">Receipt:</Typography>
              <Button
                href={receipt.url}
                download={receipt.filename}
                startIcon={
                  <IconButton
                    onClick={handleOpenLightbox}
                    sx={{ p: 0, mr: 1 }}
                    aria-label="View receipt in lightbox"
                  >
                    {getFileIcon(receipt.contentType)}
                  </IconButton>
                }
                variant="outlined"
                sx={{ mt: 1 }}
              >
                {receipt.filename}
              </Button>
            </Box>
          ) : isLoadingReceipt ? (
            <Typography sx={{ mt: 2 }}>Loading receipt...</Typography>
          ) : (
            <Typography sx={{ mt: 2 }}>No receipt available.</Typography>
          )}
          <Box sx={{ mb: 2, mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Upload Receipt
            </Typography>
            <TextField
              label="Select Receipt"
              value={selectedFile ? selectedFile.name : ""}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <Button
                    startIcon={<UploadFileIcon />}
                    onClick={handleFileButtonClick}
                    sx={{
                      textTransform: "none",
                      color: "#007bff",
                      "&:hover": { backgroundColor: "#e6f0ff" },
                    }}
                    aria-label="Browse files"
                  >
                    Browse
                  </Button>
                ),
              }}
              fullWidth
              sx={{
                mb: 1,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#007bff" },
                  "&:hover fieldset": { borderColor: "#0056b3" },
                  "&.Mui-focused fieldset": { borderColor: "#0056b3" },
                },
              }}
            />
            <Input
              type="file"
              inputProps={{ accept: ".pdf,.jpg,.jpeg,.png" }} // Removed multiple: true
              onChange={handleFileChange}
              inputRef={fileInputRef}
              sx={{ display: "none" }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary">
            Close
          </Button>
          <Button
            onClick={handleUpload}
            color="primary"
            disabled={isUploading || !selectedFile}
          >
            {isUploading ? <CircularProgress size={24} /> : "Upload Receipt"}
          </Button>
        </DialogActions>
      </Dialog>

      <Modal
        open={isLightboxOpen}
        onClose={handleCloseLightbox}
        aria-labelledby="receipt-lightbox"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "relative",
            maxWidth: "90vw",
            maxHeight: "90vh",
            bgcolor: "rgba(0, 0, 0, 0.8)",
            p: 2,
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconButton
            onClick={handleCloseLightbox}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "white",
            }}
            aria-label="Close lightbox"
          >
            <CloseIcon />
          </IconButton>
          {receipt?.url && receipt?.contentType?.startsWith("image/") && (
            <img
              src={receipt.url}
              alt="Receipt"
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
              }}
            />
          )}
        </Box>
      </Modal>
    </>
  );
};

export default VoucherModal;
